import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  CssBaseline,
  Container,
  ThemeProvider,
  createTheme,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert
} from "@mui/material";
import AppAppBar from "../../GeneralComponents/AppAppBar";
import Footer from "../NftView/components/Footer";
import Theme from "../../GeneralComponents/Theme";
import BatchNftCard from "./components/BatchNftCard";

export default function BatchNftView() {
  const [mode, setMode] = useState("light");
  const [nfts, setNfts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const theme = createTheme(Theme(mode));
  const { verificationCode } = useParams();
  const navigate = useNavigate();

  const toggleColorMode = () => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  useEffect(() => {
    const fetchBatchNFTs = async () => {
      try {
        setLoading(true);
        
        // Fetch all NFTs with this verification code
        const response = await fetch(`/api/nft/batch/${verificationCode}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            setError("No NFTs found with this verification code or they may have expired.");
            return;
          }
          throw new Error("Failed to fetch NFT details");
        }

        const data = await response.json();
        
        if (!data.nfts || data.nfts.length === 0) {
          setError("No NFTs found with this verification code.");
          return;
        }

        setNfts(data.nfts);
        
      } catch (error) {
        console.error("Error fetching batch NFTs:", error);
        setError("Failed to load NFTs. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    if (verificationCode) {
      fetchBatchNFTs();
    } else {
      setError("Invalid verification code.");
      setLoading(false);
    }
  }, [verificationCode]);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppAppBar mode={mode} toggleColorMode={toggleColorMode} />
        <Container maxWidth="lg" sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppAppBar mode={mode} toggleColorMode={toggleColorMode} />
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        </Container>
        <Footer />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppAppBar mode={mode} toggleColorMode={toggleColorMode} />
      
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Your NFT Collection
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            Successfully claimed {nfts.length} NFT{nfts.length !== 1 ? 's' : ''}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Verification Code: <strong>{verificationCode}</strong>
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {nfts.map((nft, index) => (
            <Grid item xs={12} key={nft._id || index}>
              <BatchNftCard 
                nft={nft} 
                index={index + 1}
                onViewNft={(nft) => {
                  navigate(`/nft/${nft.series}/${nft.individual}/${nft.verificationToken}`);
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
      
      <Footer />
    </ThemeProvider>
  );
} 